import React, { useCallback, useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { MAX_NAME_LENGTH } from "@web-src/modules/auth/conts";
import { Button, TextInput } from "@jugl-web/ui-kit";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

interface CXProfileFormProps {
  onSuccess: () => void;
}

export const CXProfileForm: React.FC<CXProfileFormProps> = ({ onSuccess }) => {
  const { t } = useTranslations();
  const { usersApi } = useRestApiProvider();

  const [createProfile] = usersApi.useCreateProfileMutation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleProfileSubmit = useCallback(async () => {
    setIsSubmitted(true);
    if (!firstName.trim() || !lastName.trim()) return;

    setIsLoading(true);
    try {
      await createProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      }).unwrap();
      onSuccess();
    } catch {
      // Error UI handled via meta + apiResultMiddleware
    } finally {
      setIsLoading(false);
    }
  }, [createProfile, firstName, lastName, onSuccess]);

  return (
    <div className="my-auto flex w-full max-w-[500px] flex-col items-center justify-center py-2 sm:py-4">
      <span className="text-center font-secondary text-[28px] leading-9 font-medium text-dark sm:text-[32px] sm:leading-[48px]">
        {t({
          id: "cx-ai-agents-page.lets-start-with-essentials",
          defaultMessage: "Let's start with essentials",
        })}
      </span>
      <span className="mt-2 text-center font-secondary text-sm leading-4 text-grey-900">
        {t({
          id: "cx-ai-agents-page.just-a-few-details",
          defaultMessage: "Just a few details to start with",
        })}
      </span>
      <div className="mt-6 flex w-full flex-col gap-6 sm:mt-10">
        <div className="flex w-full flex-col gap-4 sm:flex-row">
          <TextInput
            placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
            label={t({ id: "common.first-name", defaultMessage: "First Name" })}
            isRequired
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            isInvalid={isSubmitted && !firstName.trim()}
            classNames={{ wrapperClassName: "w-full sm:flex-1" }}
            maxLength={MAX_NAME_LENGTH}
          />
          <TextInput
            placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
            label={t({ id: "common.last-name", defaultMessage: "Last Name" })}
            isRequired
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            isInvalid={isSubmitted && !lastName.trim()}
            classNames={{ wrapperClassName: "w-full sm:flex-1" }}
            maxLength={MAX_NAME_LENGTH}
          />
        </div>
      </div>
      <Button
        className="mt-8 w-full sm:mt-10"
        onClick={handleProfileSubmit}
        isDisabled={!firstName.trim() || !lastName.trim() || isLoading}
        isLoading={isLoading}
        iconEnd={<ArrowIcon />}
      >
        {t({ id: "common.continue", defaultMessage: "Continue" })}
      </Button>
    </div>
  );
};
